import {useCallback, useMemo} from 'react';
import {Checkbox, FormControlLabel, Typography} from '@mui/material';
import {Dayjs} from 'dayjs';
import {daysOfWeek} from '../../../dto/DateTime.dto';
import {GetDaysFromDateRange} from '../../../components/utlis/date-time/GetDaysInDateRange';

interface DayOfWeekSelectorProps {
  rangeId: number;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  selectedDays: string[];
  onChange: (rangeId: number, days: string[]) => void;
  disabled?: boolean;
}

export const DayOfWeekSelector = ({
  rangeId,
  startDate,
  endDate,
  selectedDays,
  onChange,
  disabled = false,
}: DayOfWeekSelectorProps) => {
  // Calculate available days based on date range
  const availableDays = useMemo(() => {
    if (startDate && endDate) {
      return GetDaysFromDateRange(startDate, endDate) as string[];
    }
    return daysOfWeek;
  }, [startDate, endDate]);

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      onChange(rangeId, checked ? [...availableDays] : []);
    },
    [rangeId, availableDays, onChange],
  );

  const handleSelectDay = useCallback(
    (checked: boolean, day: string) => {
      const newDays = checked
        ? [...selectedDays, day]
        : selectedDays.filter(d => d !== day);
      onChange(rangeId, newDays);
    },
    [rangeId, selectedDays, onChange],
  );

  const allSelected = selectedDays.length === availableDays.length;

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            disabled={disabled}
            checked={allSelected}
            onChange={handleSelectAll}
          />
        }
        label={<Typography>All</Typography>}
      />
      {availableDays.map(day => {
        const shortLabel = day.substring(0, 3);
        const isChecked = selectedDays.includes(day);
        return (
          <FormControlLabel
            key={day}
            control={
              <Checkbox
                disabled={disabled}
                checked={isChecked}
                onChange={e => handleSelectDay(e.target.checked, day)}
              />
            }
            label={<Typography>{shortLabel}</Typography>}
          />
        );
      })}
    </>
  );
};
